import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { useParams, useSearchParams } from "react-router-dom";
import { BsThreeDotsVertical } from "react-icons/bs";
import { MdContentCopy, MdDeleteOutline, MdEdit, MdOutlineCheckCircle, MdOutlineEmail, MdOutlineRemoveCircleOutline, MdAddCircle, MdKeyboardArrowDown, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Utils, fetchWithAuth, selectStyles, copyToClipboard, getStaffStatusDisplay, handleSessionExpired, getDefaultPageSize, DEFAULT_STATUS_OPTIONS, can, NsPersonCell, exportListCsv } from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

/** Enabled in DB but password not set — use activation email, not manual activate/deactivate. */
function isTeamMemberPendingActivation(item) {
  return Number(item?.status) === 1 && !item?.is_member_active;
}

const AppSupportTeamMembers = () => {
  const { appId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ team_member_id: "", name: "", email: "", status: "" });
  const [draftFilters, setDraftFilters] = useState({ team_member_id: "", name: "", email: "", status: "" });
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const [form, setForm] = useState({ id: null, team_member_id: "", name: "", email: "", user_id: null, status: 0, is_app_owner: false });
  const [formErrors, setFormErrors] = useState({});

  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  /** Match rendered action buttons so dropdown position uses real height (owner has fewer). */
  const getTeamMemberActionOptionCount = (item) => {
    if (!item) return 1;
    let count = 1; // Copy Member ID
    if (can("support.team_members.edit")) count += 1;
    if (can("support.team_members.change_status") && !item.is_app_owner) count += 1;
    if (can("support.team_members.edit") && !item.is_app_owner) count += 1; // Send activation
    if (can("support.team_members.delete") && !item.is_app_owner) count += 1;
    return count;
  };

  const {
    openActionId,
    openActionItem,
    dropdownPosition,
    dropdownRef,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({
    tableScrollRef,
    optionCount: getTeamMemberActionOptionCount,
  });

  const loadData = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/team-members`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        page: nextPage,
        pageSize,
        team_member_id: (nextFilters.team_member_id || "").trim(),
        name: (nextFilters.name || "").trim(),
        email: (nextFilters.email || "").trim(),
        status: nextFilters.status,
      }),
    });
    const result = await res.json().catch(() => ({}));
    setLoading(false);
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Failed to load team members");
    setRows(result.data || []);
    setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
  };

  useEffect(() => {
    if (!appId) return;
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const team_member_id = searchParams.get("team_member_id") || "";
    const name = searchParams.get("name") || "";
    const email = searchParams.get("email") || "";
    const status = searchParams.get("status") || "";
    const f = { team_member_id, name, email, status };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(team_member_id || name || email || status));
    loadData(p, f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, searchParams]);

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.team_member_id || "").trim()) sp.set("team_member_id", f.team_member_id.trim());
    if ((f.name || "").trim()) sp.set("name", f.name.trim());
    if ((f.email || "").trim()) sp.set("email", f.email.trim());
    if (String(f.status || "").trim()) sp.set("status", String(f.status).trim());
    setSearchParams(sp, { replace: true });
  };

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(Boolean((f.team_member_id || "").trim() || (f.name || "").trim() || (f.email || "").trim() || (f.status || "").trim()));
    writeParams(1, f);
  };

  const onResetFilters = () => {
    const f = { team_member_id: "", name: "", email: "", status: "" };
    setDraftFilters(f);
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, f);
  };


  const validateForm = () => {
    const errors = {};
    const email = (form.email || "").trim();
    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const save = async () => {
    if (!validateForm()) return;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/team-member/save`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, ...form, origin: window.location.origin }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) {
      if (result.errors) {
        setFormErrors(result.errors);
      } else {
        toast.error(result.message || "Failed to save team member");
      }
      return;
    }
    toast.success(result.message || "Team member saved");
    setShowModal(false);
    setForm({ id: null, team_member_id: "", name: "", email: "", user_id: null, status: 0, is_app_owner: false });
    setFormErrors({});
    loadData();
  };

  const toggleTeamMemberStatus = async (item) => {
    if (!item?.id) return;
    const nextStatus = Number(item.status) === 1 ? 0 : 1;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/team-member/save`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        id: item.id,
        team_member_id: item.team_member_id,
        email: item.email || "",
        name: item.name || "",
        user_id: item.user_id || null,
        status: nextStatus,
        origin: window.location.origin,
      }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Failed to update team member status");
    setRows((prev) =>
      prev.map((r) => (r.id === item.id ? { ...r, status: nextStatus } : r))
    );
    if (nextStatus === 1) {
      toast.success(
        item.is_member_active
          ? "Team member activated"
          : "Team member enabled. Status is pending until they set a password via the activation email."
      );
    } else {
      toast.success("Team member deactivated");
    }
    await loadData(page, filters);
  };

  const remove = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This team member will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/team-member/delete`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, id }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Delete failed");
    toast.success("Team member deleted successfully");
    loadData();
  };

  const sendActivation = async (id) => {
    const confirm = await Swal.fire({
      title: "Send activation email?",
      text: "An activation email will be sent to this team member.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, send",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/team-member/resend-activation`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, id, origin: window.location.origin }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Failed to send activation email");
    toast.success(result.message || "Activation email sent");
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("application/support/team-members", {
        app_id: appId,
        team_member_id: (filters.team_member_id || "").trim(),
        name: (filters.name || "").trim(),
        email: (filters.email || "").trim(),
        status: filters.status,
      }, "team-members.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner ns-support-list">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Team Members</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData()}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("support.team_members.add") ? (
              <button
                type="button"
                className="add_app_btn btn_theme_primary"
                onClick={() => {
                  setForm({ id: null, team_member_id: "", name: "", email: "", user_id: null, status: 0, is_app_owner: false });
                  setFormErrors({});
                  setShowModal(true);
                }}
              >
                <MdAddCircle />
                <span>Add team member</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`} size={22} />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Member ID" value={draftFilters.team_member_id} onChange={(e) => setDraftFilters((p) => ({ ...p, team_member_id: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Name" value={draftFilters.name} onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Email" value={draftFilters.email} onChange={(e) => setDraftFilters((p) => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="support-team-members-filter-status"
                      instanceId="support-team-members-filter-status"
                      classNamePrefix="ns-support-team-members-filter-status"
                      isSearchable
                      isClearable
                      placeholder="All Status"
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => String(o.value) === String(draftFilters.status)) || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, status: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}><span>Search</span></button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}><span>Reset</span></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="4" className="text-center">Loading...</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan="4" className="text-center">No team members found</td></tr>
                    ) : rows.map((r) => (
                      <tr key={r.id}>
                        <td><NsPersonCell name={r.name} email={r.email} /></td>
                        <td>{r.email || "-"}</td>
                        <td>
                          {(() => {
                            const st = getStaffStatusDisplay(r);
                            return <span className={st.badgeClass}>{st.label}</span>;
                          })()}
                        </td>
                        <td className="col-action" style={{ textAlign: "right" }}>
                          <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                            <button
                              type="button"
                              className="dropdown-trigger-btn btn btn-link p-0"
                              aria-label="Actions"
                              aria-expanded={openActionId === r.id}
                              onClick={(e) => handleOpenDropdown(e, r.id, r)}
                            >
                              <BsThreeDotsVertical />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total || 0}
                pageSize={pageSize}
                totalPages={meta.totalPages || 1}
                ariaLabel="Team members pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {showModal ? (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.35)" }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{form.id ? "Edit team member" : "Add team member"}</h5>
                  <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={() => { setShowModal(false); setFormErrors({}); }} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label mb-1">Name</label>
                    <input className={`form-control${formErrors.name ? " is-invalid" : ""}`} value={form.name} onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setFormErrors((p) => ({ ...p, name: "" })); }} />
                    {formErrors.name && <div className="invalid-feedback d-block" style={{ marginTop: "0.25rem" }}>{formErrors.name}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label mb-1">Email <span className="text-danger">*</span></label>
                    <input
                      className={`form-control${formErrors.email ? " is-invalid" : ""}`}
                      type="email"
                      value={form.email}
                      disabled={Boolean(form.is_app_owner)}
                      title={form.is_app_owner ? "App owner email cannot be changed here" : undefined}
                      onChange={(e) => { setForm((p) => ({ ...p, email: e.target.value })); setFormErrors((p) => ({ ...p, email: "" })); }}
                    />
                    {formErrors.email && <div className="invalid-feedback d-block" style={{ marginTop: "0.25rem" }}>{formErrors.email}</div>}
                    {form.is_app_owner ? (
                      <p className="text-muted mb-0 mt-1" style={{ fontSize: "0.8rem" }}>
                        This is the app owner. Email cannot be changed from team members.
                      </p>
                    ) : null}
                  </div>
                  {form.id && !form.is_app_owner ? (
                    <div className="mb-3">
                      <label className="form-label mb-1">Status</label>
                      <Select
                        inputId="support-team-members-modal-status"
                        instanceId="support-team-members-modal-status"
                        classNamePrefix="ns-support-team-members-modal-status"
                        isSearchable={false}
                        isClearable={false}
options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => o.value === Number(form.status)) || DEFAULT_STATUS_OPTIONS[0]}
                        onChange={(opt) => setForm((p) => ({ ...p, status: Number(opt?.value ?? 1) }))}
                        styles={selectStyles}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                      />
                    </div>
                  ) : form.id && form.is_app_owner ? (
                    <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
                      App owner is always <strong>Active</strong> and cannot be deactivated.
                    </p>
                  ) : (
                    <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
                      Status will be <strong>Inactive</strong> until the member activates their account via the email link and sets a password.
                    </p>
                  )}
                  {!form.id && <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.8rem" }}>An activation email will be sent to set the password.</p>}
                  {form.id && !form.is_app_owner && <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.8rem" }}>If email is changed, status becomes inactive and a new activation email is sent.</p>}
                </div>
                <div className="modal-footer">
                  {can(form.id ? "support.team_members.edit" : "support.team_members.add") ? (
                    <button type="button" className="add_app_btn btn_theme_primary" onClick={save}><span>Save</span></button>
                  ) : null}
                  <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={() => { setShowModal(false); setFormErrors({}); }}><span>Close</span></button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {openActionItem && dropdownPosition
        ? createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 999999,
              minWidth: "220px",
            }}
          >
            <div className="dropdown-menu-subscription">
              <button
                type="button"
                onClick={() => handleDropdownAction(async () => {
                  const id = openActionItem.team_member_id;
                  if (!id) return toast.error("Member ID not available");
                  await copyToClipboard(id);
                  toast.success("Member ID copied");
                })}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdContentCopy size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Copy Member ID</span>
              </button>
              {can("support.team_members.edit") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => {
                  setForm({
                    id: openActionItem.id,
                    team_member_id: openActionItem.team_member_id,
                    name: openActionItem.name || "",
                    email: openActionItem.email || "",
                    user_id: openActionItem.user_id || null,
                    status: Number(openActionItem.status) === 0 ? 0 : 1,
                    is_app_owner: Boolean(openActionItem.is_app_owner),
                  });
                  setFormErrors({});
                  setShowModal(true);
                })}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Edit team member</span>
              </button>
              ) : null}
              {can("support.team_members.change_status") && !openActionItem.is_app_owner ? (() => {
                const toggleDisabled = isTeamMemberPendingActivation(openActionItem);
                const toggleLabel = Number(openActionItem.status) === 1 ? "Deactivate" : "Activate";
                return (
                  <button
                    type="button"
                    disabled={toggleDisabled}
                    title={
                      toggleDisabled
                        ? "Pending activation — use Send activation email; status cannot be changed manually."
                        : undefined
                    }
                    onClick={() => {
                      if (toggleDisabled) return;
                      handleDropdownAction(() => toggleTeamMemberStatus(openActionItem));
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "left",
                      opacity: toggleDisabled ? 0.45 : 1,
                      cursor: toggleDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    {Number(openActionItem.status) === 1 ? (
                      <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    ) : (
                      <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    )}
                    <span style={{ fontSize: "0.875rem" }}>{toggleLabel}</span>
                  </button>
                );
              })() : null}
              {can("support.team_members.edit") && !openActionItem.is_app_owner ? (() => {
                const activationDisabled = Boolean(openActionItem.is_member_active);
                return (
                  <button
                    type="button"
                    disabled={activationDisabled}
                    title={activationDisabled ? "Member has already completed activation." : undefined}
                    onClick={() => {
                      if (activationDisabled) return;
                      handleDropdownAction(() => sendActivation(openActionItem.id));
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "left",
                      opacity: activationDisabled ? 0.45 : 1,
                      cursor: activationDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    <MdOutlineEmail size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>Send activation email</span>
                  </button>
                );
              })() : null}
              {can("support.team_members.delete") && !openActionItem.is_app_owner ? (
              <button
                type="button"
                className="delete-class"
                onClick={() => handleDropdownAction(() => remove(openActionItem.id))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Delete team member</span>
              </button>
              ) : null}
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default AppSupportTeamMembers;
